import { Box, Tooltip, TooltipProps } from '@mui/material'
import { C1IconButton } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Check, Copy, LucideIcon } from 'lucide-react'

import { FC, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type C1CopyTextButtonProps = {
    copyValue?: string
    tooltipText?: string
    Icon?: LucideIcon
    tooltipProps?: Partial<Omit<TooltipProps, 'title' | 'children'>>
}

const defaultTooltipProps: Partial<Omit<TooltipProps, 'title' | 'children'>> = {
    placement: 'right',
}

export const C1CopyTextButton: FC<C1CopyTextButtonProps> = ({
    copyValue,
    tooltipText: _tooltipText,
    Icon = Copy,
    tooltipProps = {},
}) => {
    const intl = useIntl()
    const [clicked, setClicked] = useState(false)

    const handleClick = (text: string): void => {
        navigator.clipboard.writeText(text)
        setClicked(true)
        setTimeout(() => setClicked(false), 3000)
    }

    const tooltipText = _tooltipText || <FormattedMessage defaultMessage="Click to copy" id="app_details_app_id_copied" />
    const mergedTooltipProps = { ...defaultTooltipProps, ...tooltipProps }

    return copyValue && (!clicked
        ? <Tooltip {...mergedTooltipProps} title={tooltipText}>
            <Box>
                <C1IconButton
                    onClick={(): void => handleClick(copyValue)}
                    variant="text"
                    aria-label={intl.formatMessage({ id: 'components_copy_text_button_copy', defaultMessage: 'Copy to clipboard' })}
                >
                    <C1Icon icon={Icon} />
                </C1IconButton>
            </Box>
        </Tooltip>
        : <Tooltip
            {...mergedTooltipProps}
            title={<FormattedMessage defaultMessage="Copied" id="app_details_app_id_copied" />}
        >
            <Box>
                <C1IconButton
                    sx={{
                        color: 'inherit',
                        '&:hover, &:focus, &:active': {
                            backgroundColor: 'primary.extraLight',
                            color: 'primary.main',
                            opacity: 1,
                            outline: 'none',
                        },
                    }}
                    aria-label={intl.formatMessage({ id: 'components_copy_text_button_copied', defaultMessage: 'Copied to clipboard' })}
                >
                    <C1Icon icon={Check} />
                </C1IconButton>
            </Box>
        </Tooltip>)
}
